/** Eksportene under «venue» i katalogen (docs/EKSPORT.md §9). API-halvdelen: `apps/api/src/export/definitions/venue/`. */
import type { ExportColumnMeta, ExportMeta, ExportSectionMeta } from '../types.js';

const col = (
  group: string,
  key: string,
  type: ExportColumnMeta['type'],
  extra: Omit<ExportColumnMeta, 'key' | 'labelKey' | 'type'> = {},
): ExportColumnMeta => ({ key, labelKey: `export.venue.${group}.columns.${key}`, type, ...extra });

const section = (
  group: string,
  key: string,
  extra: Omit<ExportSectionMeta, 'key' | 'labelKey'> = {},
): ExportSectionMeta => ({ key, labelKey: `export.venue.${group}.sections.${key}`, ...extra });

/**
 * SALENS SPESIFIKASJON: det arrangøren, byrået og teknikeren ber om — mål, kapasitet og oppsett,
 * scene, teknikk, adkomst, fasiliteter, standardtider, egne felt, filer og kontakt. Fra salen i
 * huset (`object`), for alle salene (`parent` = spillestedet), og fra salens side på Spillesteder
 * for en arrangør i et annet hus: da bare det salens synlighet slipper ut (porten i 0410), aldri
 * leie, mannskapsmal eller grunnoppsett.
 */
const ROOM_SPEC: ExportMeta = {
  id: 'venue.room-spec',
  domain: 'venue',
  kind: 'document',
  titleKey: 'export.venue.roomSpec.title',
  formats: ['pdf', 'docx'],
  scopes: ['object', 'parent'],
  requires: ['venue:read'],
  guest: false,
  sections: [
    section('roomSpec', 'overview'),
    section('roomSpec', 'layouts'),
    section('roomSpec', 'stage'),
    section('roomSpec', 'technical'),
    section('roomSpec', 'logistics'),
    section('roomSpec', 'facilities'),
    section('roomSpec', 'evening'),
    section('roomSpec', 'fields'),
    section('roomSpec', 'files'),
    section('roomSpec', 'contacts'),
    /* Husets egne: står på salen i huset, aldri i det andre ser. */
    section('roomSpec', 'crewTemplate', { defaultOff: true }),
    section('roomSpec', 'baseResources', { defaultOff: true }),
    section('roomSpec', 'rate', { requires: ['venue_rate:read'], defaultOff: true }),
  ],
};

/**
 * ARRANGEMENTER FOR EN PERIODE: lista på Arrangementer med periode, sal, status og søk
 * (`filter` = `ListVenueNightsQuery` uten side), og kolonnene brukeren har valgt der
 * (`tableKey: 'venue-nights'`). `parent` er alle arrangementene på spillestedet.
 */
const EVENTS: ExportMeta = {
  id: 'venue.events',
  domain: 'venue',
  kind: 'table',
  titleKey: 'export.venue.events.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter', 'parent'],
  requires: ['venue_night:list'],
  guest: false,
  tableKey: 'venue-nights',
  columns: [
    col('events', 'date', 'date'),
    col('events', 'what', 'text'),
    col('events', 'title', 'text', { defaultHidden: true }),
    col('events', 'artist', 'text', { defaultHidden: true }),
    col('events', 'room', 'text'),
    col('events', 'promoter', 'text'),
    col('events', 'times', 'text'),
    col('events', 'doors', 'time', { defaultHidden: true }),
    col('events', 'curfew', 'time', { defaultHidden: true }),
    col('events', 'getIn', 'time', { defaultHidden: true }),
    col('events', 'layout', 'text', { defaultHidden: true }),
    col('events', 'capacity', 'integer', { defaultHidden: true }),
    col('events', 'status', 'status'),
    col('events', 'crew', 'integer'),
    col('events', 'series', 'boolean', { defaultHidden: true }),
    col('events', 'notes', 'multiline', { defaultHidden: true }),
    col('events', 'createdAt', 'datetime', { defaultHidden: true }),
    col('events', 'updatedAt', 'datetime', { defaultHidden: true }),
  ],
  params: ['festivalId'],
  landscape: true,
};

/**
 * DAGSARK FOR ET ARRANGEMENT: ett ark per arrangement med fakta, tidsplanen (get-in, dører, curfew
 * og programpostens tider), vaktene med folk og telefon, ressursene og kontaktene. Fra
 * arrangementet (`object`) og for arrangementene i lista (`filter`, samme filter som tabellen).
 */
const DAY_SHEET: ExportMeta = {
  id: 'venue.day-sheet',
  domain: 'venue',
  kind: 'document',
  titleKey: 'export.venue.daySheet.title',
  formats: ['pdf', 'docx'],
  scopes: ['object', 'filter'],
  requires: ['venue_night:list', 'venue_night:read'],
  guest: false,
  sections: [
    section('daySheet', 'facts'),
    section('daySheet', 'schedule'),
    section('daySheet', 'crew', { requires: ['shift:list'] }),
    section('daySheet', 'resources', { requires: ['allocation:list'] }),
    section('daySheet', 'contacts', { requires: ['performance:read'] }),
  ],
  params: ['festivalId'],
};

/**
 * DATOFORESPØRSLER OG HOLD: tabellen på Datoforespørsler med fanen og søket (`filter`) og
 * kolonnene brukeren har valgt (`tableKey: 'venue-requests'`). `parent` er alle fire fanene i én
 * tabell, gruppert på fanen. Leien står som på skjermen: der den som ser lista, ser den.
 */
const ENQUIRIES: ExportMeta = {
  id: 'venue.enquiries',
  domain: 'venue',
  kind: 'table',
  titleKey: 'export.venue.enquiries.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter', 'parent'],
  requires: ['venue_enquiry:list'],
  guest: false,
  tableKey: 'venue-requests',
  columns: [
    col('enquiries', 'date', 'date'),
    col('enquiries', 'room', 'text'),
    col('enquiries', 'requester', 'text'),
    col('enquiries', 'artist', 'text'),
    col('enquiries', 'layout', 'text'),
    col('enquiries', 'status', 'status'),
    col('enquiries', 'expires', 'date'),
    col('enquiries', 'tab', 'status', { defaultHidden: true }),
    col('enquiries', 'sentBy', 'text', { defaultHidden: true }),
    col('enquiries', 'audience', 'integer', { defaultHidden: true }),
    col('enquiries', 'doors', 'time', { defaultHidden: true }),
    col('enquiries', 'curfew', 'time', { defaultHidden: true }),
    /* 251.7: leien, vilkårene og merch-andelen krever leietilgangen, som på skjermen. */
    col('enquiries', 'rent', 'money', { defaultHidden: true, requires: ['venue_rate:read'] }),
    col('enquiries', 'message', 'multiline', { defaultHidden: true }),
    col('enquiries', 'answeredAt', 'datetime', { defaultHidden: true }),
    col('enquiries', 'requestedOn', 'date', { defaultHidden: true }),
    col('enquiries', 'capacity', 'integer', { defaultHidden: true }),
    col('enquiries', 'rentTerms', 'multiline', {
      defaultHidden: true,
      requires: ['venue_rate:read'],
    }),
    col('enquiries', 'merch', 'decimal', { defaultHidden: true, requires: ['venue_rate:read'] }),
    col('enquiries', 'answerNote', 'multiline', { defaultHidden: true }),
    col('enquiries', 'received', 'datetime', { defaultHidden: true }),
  ],
  params: ['festivalId'],
  landscape: true,
};

/**
 * BOOKINGKALENDER PER SAL: én del per sal med én tabell gruppert på dato — arrangementene, og
 * holdene og forespørslene som venter (for den som ser Datoforespørsler), med stengte ukedager
 * merket. Perioden er `params.from`/`to` (standard: i dag og tre måneder fram).
 */
const ROOM_CALENDAR: ExportMeta = {
  id: 'venue.room-calendar',
  domain: 'venue',
  kind: 'document',
  titleKey: 'export.venue.roomCalendar.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object', 'parent'],
  requires: ['venue:read', 'venue_night:list'],
  guest: false,
  params: ['festivalId'],
  landscape: true,
};

/** MANNSKAPET: husets folk med post, telefon, e-post, kommende vakter og notat. */
const CREW: ExportMeta = {
  id: 'venue.crew',
  domain: 'venue',
  kind: 'table',
  titleKey: 'export.venue.crew.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['parent'],
  requires: ['crew_member:list'],
  guest: false,
  columns: [
    col('crew', 'name', 'text'),
    col('crew', 'post', 'text'),
    col('crew', 'phone', 'phone'),
    col('crew', 'email', 'email'),
    col('crew', 'upcoming', 'integer'),
    col('crew', 'note', 'multiline'),
  ],
  params: ['festivalId'],
};

/** POSTENE (F39): navn, standard antall, bruk i salenes mannskapsmal og kommende vakter. */
const POSTS: ExportMeta = {
  id: 'venue.posts',
  domain: 'venue',
  kind: 'table',
  titleKey: 'export.venue.posts.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['parent'],
  requires: ['shift:list'],
  guest: false,
  columns: [
    col('posts', 'name', 'text'),
    col('posts', 'defaultNeeded', 'integer'),
    col('posts', 'templateCount', 'integer'),
    col('posts', 'upcomingShifts', 'integer'),
  ],
  params: ['festivalId'],
};

/**
 * VAKTPLANEN PÅ TVERS AV ARRANGEMENTENE: én rad per vakt (per person når den grupperes på
 * person), gruppert etter `params.groupBy` — `event` (standard), `day`, `post` eller `person`.
 * `filter` er uka på Vaktplan (`from`, `to`, `onlyGaps`); `parent` en periode i `params`
 * (standard: i dag og fire uker fram).
 */
const SHIFT_PLAN: ExportMeta = {
  id: 'venue.shift-plan',
  domain: 'venue',
  kind: 'table',
  titleKey: 'export.venue.shiftPlan.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter', 'parent'],
  requires: ['shift:list'],
  guest: false,
  columns: [
    col('shiftPlan', 'day', 'date'),
    col('shiftPlan', 'event', 'text'),
    col('shiftPlan', 'room', 'text'),
    col('shiftPlan', 'start', 'time'),
    col('shiftPlan', 'end', 'time'),
    col('shiftPlan', 'post', 'text'),
    col('shiftPlan', 'people', 'multiline'),
    col('shiftPlan', 'staffed', 'text'),
    col('shiftPlan', 'missing', 'integer', { defaultHidden: true }),
    col('shiftPlan', 'person', 'text', { defaultHidden: true }),
    col('shiftPlan', 'status', 'status', { defaultHidden: true }),
    col('shiftPlan', 'phone', 'phone', { defaultHidden: true }),
  ],
  params: ['festivalId'],
  landscape: true,
};

/** RESSURSENE: husets register uten årgang, med søket og «Vis arkiverte» fra siden (`filter`). */
const RESOURCES: ExportMeta = {
  id: 'venue.resources',
  domain: 'venue',
  kind: 'table',
  titleKey: 'export.venue.resources.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter', 'parent'],
  requires: ['resource:list'],
  guest: false,
  columns: [
    col('resources', 'name', 'text'),
    col('resources', 'subtitle', 'text', { defaultHidden: true }),
    col('resources', 'type', 'text'),
    col('resources', 'capacity', 'integer'),
    col('resources', 'unit', 'text', { defaultHidden: true }),
    col('resources', 'rooms', 'text'),
    col('resources', 'notes', 'multiline', { defaultHidden: true }),
    col('resources', 'archived', 'boolean', { defaultHidden: true }),
  ],
  params: ['festivalId'],
};

/**
 * RESERVASJONENE PÅ ARRANGEMENTENE: én rad per reservasjon, gruppert per arrangement (standard)
 * eller per ressurs (`params.groupBy`). Ett arrangement (`object`), arrangementene i lista
 * (`filter`), eller en periode (`parent`, `params.from`/`to`).
 */
const RESERVATIONS: ExportMeta = {
  id: 'venue.reservations',
  domain: 'venue',
  kind: 'table',
  titleKey: 'export.venue.reservations.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object', 'filter', 'parent'],
  requires: ['allocation:list', 'venue_night:list'],
  guest: false,
  columns: [
    col('reservations', 'date', 'date'),
    col('reservations', 'event', 'text'),
    col('reservations', 'room', 'text'),
    col('reservations', 'resource', 'text'),
    col('reservations', 'type', 'text', { defaultHidden: true }),
    col('reservations', 'quantity', 'integer'),
    col('reservations', 'from', 'time'),
    col('reservations', 'to', 'time'),
    col('reservations', 'source', 'text'),
    col('reservations', 'conflicts', 'multiline'),
    col('reservations', 'note', 'multiline', { defaultHidden: true }),
  ],
  params: ['festivalId'],
  landscape: true,
};

/**
 * LEIE OG ØKONOMI: salenes leie og vilkår, leien som er avtalt per arrangement i perioden (fra den
 * bekreftede datoforespørselen), og honoraret i kontrakten der arrangementet har en programpost.
 * Bare for den som ser leien (`venue_rate:read`); honoraret bare med `contract:read`.
 */
const ECONOMY: ExportMeta = {
  id: 'venue.economy',
  domain: 'venue',
  kind: 'document',
  titleKey: 'export.venue.economy.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['parent'],
  requires: ['venue_rate:read'],
  guest: false,
  sections: [
    section('economy', 'rates', { requires: ['venue_rate:read'] }),
    section('economy', 'events', { requires: ['venue_rate:read', 'venue_enquiry:read'] }),
    section('economy', 'fees', { requires: ['contract:read'] }),
  ],
  params: ['festivalId'],
  landscape: true,
};

export const VENUE_EXPORTS: readonly ExportMeta[] = [
  ROOM_SPEC,
  EVENTS,
  DAY_SHEET,
  ENQUIRIES,
  ROOM_CALENDAR,
  CREW,
  POSTS,
  SHIFT_PLAN,
  RESOURCES,
  RESERVATIONS,
  ECONOMY,
];
